import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save, LogOut, RefreshCw, Check, X, Edit2, ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    // Check if already authenticated (stored in session)
    const storedAuth = sessionStorage.getItem('admin_auth');
    if (storedAuth) {
      setIsAuthenticated(true);
      setPassword(storedAuth);
      fetchAllContent(storedAuth);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      
      if (response.ok) {
        setIsAuthenticated(true);
        sessionStorage.setItem('admin_auth', password);
        toast.success('Login successful');
        fetchAllContent(password);
      } else {
        toast.error('Invalid password');
      }
    } catch (error) {
      toast.error('Login failed');
    }
    setLoading(false);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    setContent({});
    sessionStorage.removeItem('admin_auth');
    toast.success('Logged out');
  };

  const fetchAllContent = async (pwd) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/all-content`);
      const data = await response.json();
      setContent(data);
    } catch (error) {
      toast.error('Failed to fetch content');
    }
    setLoading(false);
  };

  const initializeDefaults = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/init-defaults?password=${password}`, {
        method: 'POST'
      });
      const data = await response.json();
      toast.success(`Initialized: ${data.initialized.join(', ') || 'All sections already exist'}`);
      fetchAllContent(password);
    } catch (error) {
      toast.error('Failed to initialize');
    }
    setLoading(false);
  };

  const saveSection = async (section) => {
    setSaving(prev => ({ ...prev, [section]: true }));
    try {
      const response = await fetch(`${API_URL}/api/admin/content/${section}?password=${password}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ section, content: content[section] })
      });
      
      if (response.ok) {
        toast.success(`${section} saved successfully`);
      } else {
        toast.error(`Failed to save ${section}`);
      }
    } catch (error) {
      toast.error(`Error saving ${section}`);
    }
    setSaving(prev => ({ ...prev, [section]: false }));
  };

  const updateContent = (section, key, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
  };

  const updateArrayItem = (section, index, key, value) => {
    setContent(prev => {
      const newArray = [...prev[section]];
      newArray[index] = { ...newArray[index], [key]: value };
      return { ...prev, [section]: newArray };
    });
  };

  const updateNestedArrayItem = (section, arrayKey, index, key, value) => {
    setContent(prev => {
      const sectionData = { ...prev[section] };
      const newArray = [...(sectionData[arrayKey] || [])];
      newArray[index] = { ...newArray[index], [key]: value };
      sectionData[arrayKey] = newArray;
      return { ...prev, [section]: sectionData };
    });
  };

  const addNestedArrayItem = (section, arrayKey, template) => {
    setContent(prev => {
      const sectionData = { ...prev[section] };
      const newArray = [...(sectionData[arrayKey] || []), template];
      sectionData[arrayKey] = newArray;
      return { ...prev, [section]: sectionData };
    });
  };

  const removeNestedArrayItem = (section, arrayKey, index) => {
    setContent(prev => {
      const sectionData = { ...prev[section] };
      const newArray = [...(sectionData[arrayKey] || [])];
      newArray.splice(index, 1);
      sectionData[arrayKey] = newArray;
      return { ...prev, [section]: sectionData };
    });
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-white/[0.03] flex items-center justify-center p-4">
        <SEO title="Admin" noindex={true} path="/admin" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#050505] rounded-2xl shadow-xl p-8 w-full max-w-md"
        >
          <h1 className="text-2xl font-bold text-white mb-2">Admin Login</h1>
          <p className="text-[#71717A] mb-6">Enter your admin password to manage content</p>
          
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              className="w-full px-4 py-3 border border-white/10 rounded-lg mb-4 focus:outline-none focus:border-[#333]"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-white text-black rounded-lg font-medium hover:bg-[#222] disabled:opacity-50"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white/[0.03]">
      <SEO title="Admin CMS" noindex={true} path="/admin" />
      <div className="bg-white text-black px-6 py-4 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Agentic Atoms CMS</h1>
            <p className="text-sm text-gray-300">Manage your website content</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={initializeDefaults}
              className="flex items-center gap-2 px-4 py-2 bg-[#050505]/10 rounded-lg hover:bg-[#050505]/20 text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              Init Defaults
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-red-500/20 rounded-lg hover:bg-red-500/30 text-sm"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {loading ? (
          <div className="text-center py-20">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#71717A]" />
            <p className="mt-4 text-[#71717A]">Loading content...</p>
          </div>
        ) : (
          <>
            {/* Hero Section */}
            <SectionCard
              title="Hero Section"
              section="hero"
              expanded={expandedSections.hero}
              onToggle={() => toggleSection('hero')}
              onSave={() => saveSection('hero')}
              saving={saving.hero}
            >
              {content.hero && (
                <div className="space-y-4">
                  <InputField
                    label="Title"
                    value={content.hero.title || ''}
                    onChange={(v) => updateContent('hero', 'title', v)}
                  />
                  <InputField
                    label="Subtitle"
                    value={content.hero.subtitle || ''}
                    onChange={(v) => updateContent('hero', 'subtitle', v)}
                  />
                  <TextAreaField
                    label="Description"
                    value={content.hero.description || ''}
                    onChange={(v) => updateContent('hero', 'description', v)}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <InputField
                      label="Primary CTA"
                      value={content.hero.cta_primary || ''}
                      onChange={(v) => updateContent('hero', 'cta_primary', v)}
                    />
                    <InputField
                      label="Secondary CTA"
                      value={content.hero.cta_secondary || ''}
                      onChange={(v) => updateContent('hero', 'cta_secondary', v)}
                    />
                  </div>
                </div>
              )}
            </SectionCard>

            {/* About Section */}
            <SectionCard
              title="About Section"
              section="about"
              expanded={expandedSections.about}
              onToggle={() => toggleSection('about')}
              onSave={() => saveSection('about')}
              saving={saving.about}
            >
              {content.about && (
                <div className="space-y-4">
                  <InputField
                    label="Badge Text"
                    value={content.about.badge || ''}
                    onChange={(v) => updateContent('about', 'badge', v)}
                  />
                  <InputField
                    label="Title"
                    value={content.about.title || ''}
                    onChange={(v) => updateContent('about', 'title', v)}
                  />
                  <TextAreaField
                    label="Description"
                    value={content.about.description || ''}
                    onChange={(v) => updateContent('about', 'description', v)}
                    rows={5}
                  />
                </div>
              )}
            </SectionCard>

            {/* Contact Info */}
            <SectionCard
              title="Contact Information"
              section="contact"
              expanded={expandedSections.contact}
              onToggle={() => toggleSection('contact')}
              onSave={() => saveSection('contact')}
              saving={saving.contact}
            >
              {content.contact && (
                <div className="space-y-4">
                  <InputField
                    label="Email"
                    value={content.contact.email || ''}
                    onChange={(v) => updateContent('contact', 'email', v)}
                  />
                  <InputField
                    label="Phone"
                    value={content.contact.phone || ''}
                    onChange={(v) => updateContent('contact', 'phone', v)}
                  />
                  <InputField
                    label="Address"
                    value={content.contact.address || ''}
                    onChange={(v) => updateContent('contact', 'address', v)}
                  />
                  <InputField
                    label="Form Title"
                    value={content.contact.form_title || ''}
                    onChange={(v) => updateContent('contact', 'form_title', v)}
                  />
                  <TextAreaField
                    label="Form Subtitle"
                    value={content.contact.form_subtitle || ''}
                    onChange={(v) => updateContent('contact', 'form_subtitle', v)}
                  />
                </div>
              )}
            </SectionCard>

            {/* Stats */}
            <SectionCard
              title="Stats / Highlights"
              section="stats"
              expanded={expandedSections.stats}
              onToggle={() => toggleSection('stats')}
              onSave={() => saveSection('stats')}
              saving={saving.stats}
            >
              {content.stats && Array.isArray(content.stats) && (
                <div className="space-y-4">
                  {content.stats.map((stat, index) => (
                    <div key={stat.id || index} className="p-4 bg-white/[0.03] rounded-lg">
                      <div className="grid grid-cols-2 gap-4">
                        <InputField
                          label="Value"
                          value={stat.value || ''}
                          onChange={(v) => updateArrayItem('stats', index, 'value', v)}
                        />
                        <InputField
                          label="Label"
                          value={stat.label || ''}
                          onChange={(v) => updateArrayItem('stats', index, 'label', v)}
                        />
                      </div>
                      <InputField
                        label="Description"
                        value={stat.description || ''}
                        onChange={(v) => updateArrayItem('stats', index, 'description', v)}
                        className="mt-3"
                      />
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>

            {/* How It Works Section */}
            <SectionCard
              title="How It Works"
              section="how_it_works"
              expanded={expandedSections.how_it_works}
              onToggle={() => toggleSection('how_it_works')}
              onSave={() => saveSection('how_it_works')}
              saving={saving.how_it_works}
            >
              {content.how_it_works && (
                <div className="space-y-4">
                  <InputField
                    label="Badge Text"
                    value={content.how_it_works.badge || ''}
                    onChange={(v) => updateContent('how_it_works', 'badge', v)}
                  />
                  <InputField
                    label="Section Title"
                    value={content.how_it_works.title || ''}
                    onChange={(v) => updateContent('how_it_works', 'title', v)}
                  />
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-[#A1A1AA] mb-2">Steps</label>
                    {(content.how_it_works.steps || []).map((step, index) => (
                      <div key={index} className="p-4 bg-white/[0.03] rounded-lg mb-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-bold text-[#71717A]">Step {step.number || index + 1}</span>
                          {(content.how_it_works.steps || []).length > 1 && (
                            <button onClick={() => removeNestedArrayItem('how_it_works', 'steps', index)} className="text-red-400 hover:text-red-600">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <InputField label="Title" value={step.title || ''} onChange={(v) => updateNestedArrayItem('how_it_works', 'steps', index, 'title', v)} />
                          <InputField label="Timeline" value={step.timeline || ''} onChange={(v) => updateNestedArrayItem('how_it_works', 'steps', index, 'timeline', v)} />
                        </div>
                        <TextAreaField label="Description" value={step.description || ''} onChange={(v) => updateNestedArrayItem('how_it_works', 'steps', index, 'description', v)} rows={2} />
                      </div>
                    ))}
                    <button
                      onClick={() => addNestedArrayItem('how_it_works', 'steps', { number: String((content.how_it_works.steps || []).length + 1).padStart(2, '0'), title: '', timeline: '', description: '' })}
                      className="flex items-center gap-1.5 text-sm text-[#71717A] hover:text-[#A1A1AA] mt-2"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Step
                    </button>
                  </div>
                </div>
              )}
            </SectionCard>

            {/* Testimonials Section */}
            <SectionCard
              title="Testimonials & Social Proof"
              section="testimonials"
              expanded={expandedSections.testimonials}
              onToggle={() => toggleSection('testimonials')}
              onSave={() => saveSection('testimonials')}
              saving={saving.testimonials}
            >
              {content.testimonials && (
                <div className="space-y-4">
                  <InputField
                    label="Badge Text"
                    value={content.testimonials.badge || ''}
                    onChange={(v) => updateContent('testimonials', 'badge', v)}
                  />
                  <InputField
                    label="Section Title"
                    value={content.testimonials.title || ''}
                    onChange={(v) => updateContent('testimonials', 'title', v)}
                  />
                  
                  {/* Stats */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-[#A1A1AA] mb-2">Highlight Stats</label>
                    {(content.testimonials.stats || []).map((stat, index) => (
                      <div key={index} className="p-3 bg-white/[0.03] rounded-lg mb-2 flex items-end gap-3">
                        <InputField label="Value" value={stat.value || ''} onChange={(v) => updateNestedArrayItem('testimonials', 'stats', index, 'value', v)} className="flex-1" />
                        <InputField label="Label" value={stat.label || ''} onChange={(v) => updateNestedArrayItem('testimonials', 'stats', index, 'label', v)} className="flex-[2]" />
                        {(content.testimonials.stats || []).length > 1 && (
                          <button onClick={() => removeNestedArrayItem('testimonials', 'stats', index)} className="text-red-400 hover:text-red-600 pb-2">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={() => addNestedArrayItem('testimonials', 'stats', { value: '', label: '' })}
                      className="flex items-center gap-1.5 text-sm text-[#71717A] hover:text-[#A1A1AA] mt-1"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Stat
                    </button>
                  </div>

                  {/* Testimonial Items */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-[#A1A1AA] mb-2">Testimonials</label>
                    {(content.testimonials.items || []).map((item, index) => (
                      <div key={index} className="p-4 bg-white/[0.03] rounded-lg mb-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-bold text-[#71717A]">Testimonial {index + 1}</span>
                          {(content.testimonials.items || []).length > 1 && (
                            <button onClick={() => removeNestedArrayItem('testimonials', 'items', index)} className="text-red-400 hover:text-red-600">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                        <TextAreaField label="Quote" value={item.quote || ''} onChange={(v) => updateNestedArrayItem('testimonials', 'items', index, 'quote', v)} rows={2} />
                        <div className="grid grid-cols-3 gap-3 mt-2">
                          <InputField label="Author" value={item.author || ''} onChange={(v) => updateNestedArrayItem('testimonials', 'items', index, 'author', v)} />
                          <InputField label="Role" value={item.role || ''} onChange={(v) => updateNestedArrayItem('testimonials', 'items', index, 'role', v)} />
                          <InputField label="Industry" value={item.industry || ''} onChange={(v) => updateNestedArrayItem('testimonials', 'items', index, 'industry', v)} />
                        </div>
                      </div>
                    ))}
                    <button
                      onClick={() => addNestedArrayItem('testimonials', 'items', { quote: '', author: '', role: '', industry: '' })}
                      className="flex items-center gap-1.5 text-sm text-[#71717A] hover:text-[#A1A1AA] mt-2"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Testimonial
                    </button>
                  </div>
                </div>
              )}
            </SectionCard>

            {/* FAQ Section */}
            <SectionCard
              title="FAQ"
              section="faq"
              expanded={expandedSections.faq}
              onToggle={() => toggleSection('faq')}
              onSave={() => saveSection('faq')}
              saving={saving.faq}
            >
              {content.faq && (
                <div className="space-y-4">
                  <InputField
                    label="Badge Text"
                    value={content.faq.badge || ''}
                    onChange={(v) => updateContent('faq', 'badge', v)}
                  />
                  <InputField
                    label="Section Title"
                    value={content.faq.title || ''}
                    onChange={(v) => updateContent('faq', 'title', v)}
                  />
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-[#A1A1AA] mb-2">Questions & Answers</label>
                    {(content.faq.items || []).map((item, index) => (
                      <div key={index} className="p-4 bg-white/[0.03] rounded-lg mb-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-bold text-[#71717A]">Q{index + 1}</span>
                          {(content.faq.items || []).length > 1 && (
                            <button onClick={() => removeNestedArrayItem('faq', 'items', index)} className="text-red-400 hover:text-red-600">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                        <InputField label="Question" value={item.question || ''} onChange={(v) => updateNestedArrayItem('faq', 'items', index, 'question', v)} />
                        <TextAreaField label="Answer" value={item.answer || ''} onChange={(v) => updateNestedArrayItem('faq', 'items', index, 'answer', v)} rows={2} />
                      </div>
                    ))}
                    <button
                      onClick={() => addNestedArrayItem('faq', 'items', { question: '', answer: '' })}
                      className="flex items-center gap-1.5 text-sm text-[#71717A] hover:text-[#A1A1AA] mt-2"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Question
                    </button>
                  </div>
                </div>
              )}
            </SectionCard>

            {/* Footer */}
            <SectionCard
              title="Footer"
              section="footer"
              expanded={expandedSections.footer}
              onToggle={() => toggleSection('footer')}
              onSave={() => saveSection('footer')}
              saving={saving.footer}
            >
              {content.footer && (
                <div className="space-y-4">
                  <TextAreaField
                    label="Tagline"
                    value={content.footer.tagline || ''}
                    onChange={(v) => updateContent('footer', 'tagline', v)}
                  />
                  <InputField
                    label="Copyright Text"
                    value={content.footer.copyright || ''}
                    onChange={(v) => updateContent('footer', 'copyright', v)}
                  />
                </div>
              )}
            </SectionCard>
          </>
        )}
      </div>
    </div>
  );
};

// Reusable Components
const SectionCard = ({ title, section, expanded, onToggle, onSave, saving, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-[#050505] rounded-xl shadow-sm border border-white/[0.08] overflow-hidden"
  >
    <div
      onClick={onToggle}
      className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/[0.03]"
    >
      <div className="flex items-center gap-3">
        <Edit2 className="w-5 h-5 text-[#71717A]" />
        <h2 className="font-semibold text-white">{title}</h2>
      </div>
      <div className="flex items-center gap-2">
        {expanded && (
          <button
            onClick={(e) => { e.stopPropagation(); onSave(); }}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-black rounded-lg text-sm hover:bg-[#222] disabled:opacity-50"
          >
            {saving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Save
          </button>
        )}
        {expanded ? <ChevronUp className="w-5 h-5 text-[#71717A]" /> : <ChevronDown className="w-5 h-5 text-[#71717A]" />}
      </div>
    </div>
    {expanded && <div className="p-4 pt-0 border-t border-white/[0.08]">{children}</div>}
  </motion.div>
);

const InputField = ({ label, value, onChange, className = '' }) => (
  <div className={className}>
    <label className="block text-sm font-medium text-[#A1A1AA] mb-1">{label}</label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-white/10 rounded-lg focus:outline-none focus:border-[#333]"
    />
  </div>
);

const TextAreaField = ({ label, value, onChange, rows = 3 }) => (
  <div>
    <label className="block text-sm font-medium text-[#A1A1AA] mb-1">{label}</label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      className="w-full px-3 py-2 border border-white/10 rounded-lg focus:outline-none focus:border-[#333] resize-none"
    />
  </div>
);

export default AdminPage;
